import axios from "axios";
import { getClaims } from "../../../modules/login";
import moment from "moment";
import { themes } from "../../../modules/theme";
import {
  decimalPlaces,
  getFinalKcalAndPercentage,
  roundMealValue,
} from "../../../helpers/mealsHelper";
import {
  dailyTargetIds,
  DateFormat,
  getMacronutrientColor,
  getTargetsBgColor,
  studyStatusesGSDIII,
} from "../../../modules/constants";
import i18n from "../../../i18n";

//Constants
const SET_GOALS = "SET_GOALS";
const LOGOUT = "LOGOUT";
const RESET_STATE = "RESET_STATE";

//Acessors
const getGoalsList = (state) => state?.goalsList;
const getGoalsStatus = (state) => state?.status;
const getCurrentDailyTargets = (state) => state?.currentDailyTargets;

const canViewTargetsHistory = (state) =>
  getClaims(state, "participant.target.history.canView");
const canCreateNewTarget = (state) =>
  getClaims(state, "participant.target.canCreate");

//Default state
const initialState = {
  goalsList: null,
  currentDailyTargets: null,
  status: null,
};

export const targetsReducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_GOALS: {
      const { goals, currentDailyTargets } = action;
      return Object.assign({}, state, {
        status: true,
        goalsList: goals,
        currentDailyTargets: currentDailyTargets,
      });
    }
    case RESET_STATE:
      return initialState;
    case LOGOUT:
      return initialState;
    default:
      return state;
  }
};

const resetState = () => (dispatch) => {
  dispatch({ type: RESET_STATE });
};

const updateDailyTargets =
  (participantId, dailyTarget, reasonForChange) => (dispatch, getState) => {
    const body = {
      participantId: participantId,
      dailyTarget: dailyTarget,
      reasonForChange: reasonForChange,
    };

    return axios
      .put(`/api/diet/dailytarget`, body)
      .then(() => {
        return Promise.resolve("Daily target updated successfully!");
      })
      .catch((e) => {
        return Promise.reject(
          e.response?.data
            ? e.response.data
            : "There was an error while trying to update the daily target!"
        );
      });
  };

const fetchGoals =
  (
    startDate,
    endDate,
    participantId = null,
    studyId = null,
    studyStatus = null,
    t
  ) =>
  (dispatch, getState) => {
    const params = {
      StartDate: startDate,
      EndDate: endDate,
    };

    if (participantId) {
      params.ParticipantId = participantId;
    }

    return axios
      .get(`/api/diet/meals/`, { params })
      .then(({ data }) => {
        dispatch({
          type: SET_GOALS,
          goals: parseGoalsData(data, studyId, studyStatus, t),
          currentDailyTargets: data?.dailyTargets,
        });
      })
      .catch(() => {
        dispatch({ type: SET_GOALS, goals: null });

        return Promise.reject(i18n.t("errorText.dietDiary.dailyTargets.get"));
      });
  };

const parseGoalsData = (data, studyId, studyStatus, t) => {
  const { dailyTargets, meals } = data;

  let formattedData = [];

  meals
    .filter((item) => item.completed)
    .forEach((entry) => {
      const { mealDate, mealJson } = entry;
      const mDate = moment(mealDate).format(DateFormat.EXTENDED_FORMAT);
      const items = JSON.parse(mealJson);
      const mealObj = {
        time: moment(mealDate).format("H:mm A"),
        calories: { ...items.aboutMeal.Calories },
        protein: { ...items.aboutMeal.Protein },
        carbohydrates: { ...items.aboutMeal.Carbs },
        fats: { ...items.aboutMeal.Fats },
        json: mealJson,
        dateTime: mealDate,
      };

      const dayObj = {
        date: mDate,
      };
      //Check if an object with this date already exists
      const dateAtIndex = formattedData.findIndex((d) => d.date === mDate);

      if (dateAtIndex >= 0) {
        //Add this meal if object exists
        formattedData[dateAtIndex].meals.push(mealObj);
        //Update day values with all existing meals!
        const mealDaySum = mealsToDay(formattedData[dateAtIndex].meals);
        formattedData[dateAtIndex].calories = mealDaySum.calories;
        formattedData[dateAtIndex].protein = mealDaySum.protein;
        formattedData[dateAtIndex].carbohydrates = mealDaySum.carbohydrates;
        formattedData[dateAtIndex].fats = mealDaySum.fats;
      } else {
        //This is the only meal
        dayObj.meals = [mealObj];
        //Day's value are of this meal only
        dayObj.calories = mealObj.calories;
        dayObj.protein = mealObj.protein;
        dayObj.carbohydrates = mealObj.carbohydrates;
        dayObj.fats = mealObj.fats;
        formattedData.push(dayObj);
      }
    });

  const kcalObj = getFinalKcalAndPercentage(formattedData);

  const averageGoals = getAverageGoals(kcalObj);

  return {
    CALORIES: {
      title:
        studyStatus === studyStatusesGSDIII.SAD_PERIOD.id
          ? ""
          : t("appText.dietDiary.targets.target"),
      origin:
        studyStatus === studyStatusesGSDIII.SAD_PERIOD.id
          ? "kcal"
          : `${Number(roundMealValue(dailyTargets?.calories ?? 0)).toFixed(
              decimalPlaces
            )} kcal`,
      color: themes.default.darkPurple,
      bgColor: themes.default.soulDance,
      label: t("appText.dietDiary.dailyAverage"),
      value: `${Number(roundMealValue(averageGoals.calories.value)).toFixed(
        decimalPlaces
      )} kcal`,
    },
    PROTEIN: {
      title:
        studyStatus === studyStatusesGSDIII.SAD_PERIOD.id
          ? ""
          : `${t("appText.moreThan")} ${dailyTargets?.proteinPercentage ?? 0}%`,
      origin: `${t("appText.from")} ${t("appText.dietDiary.protein")}`,
      color: getMacronutrientColor(
        dailyTargetIds.PROTEIN_ATTR_ID,
        themes.default,
        studyId
      ),
      bgColor: getTargetsBgColor(
        dailyTargetIds.PROTEIN_ATTR_ID,
        themes.default,
        studyId
      ),
      label: t("appText.dietDiary.dailyAverage"),
      value: `${Number(roundMealValue(averageGoals.protein.value)).toFixed(
        decimalPlaces
      )}${averageGoals.protein.unit} - ${Number(
        roundMealValue(averageGoals.protein.percentage)
      ).toFixed(decimalPlaces)}%`,
    },
    CARBOHYDRATES: {
      title:
        studyStatus === studyStatusesGSDIII.SAD_PERIOD.id
          ? ""
          : `${t("appText.lessThan")} ${
              dailyTargets?.carbohydratesPercentage ?? 0
            }%`,
      origin: `${t("appText.from")} ${t("appText.dietDiary.carbohydrates")}`,
      color: getMacronutrientColor(
        dailyTargetIds.CARBS_ATTR_ID,
        themes.default,
        studyId
      ),
      bgColor: getTargetsBgColor(
        dailyTargetIds.CARBS_ATTR_ID,
        themes.default,
        studyId
      ),
      label: t("appText.dietDiary.dailyAverage"),
      value: `${Number(
        roundMealValue(averageGoals.carbohydrates.value)
      ).toFixed(decimalPlaces)}${averageGoals.carbohydrates.unit} - ${Number(
        roundMealValue(averageGoals.carbohydrates.percentage)
      ).toFixed(decimalPlaces)}%`,
    },
  };
};

const mealsToDay = (array = [], key) => {
  const tempData = array.reduce((p, c) => {
    return {
      calories: {
        percentage: p.calories.percentage + c.calories.percentage,
        value: p.calories.value + c.calories.value,
        unit: c.calories.unit,
      },
      protein: {
        percentage: p.protein.percentage + c.protein.percentage,
        value: p.protein.value + c.protein.value,
        unit: c.protein.unit,
      },
      carbohydrates: {
        percentage: p.carbohydrates.percentage + c.carbohydrates.percentage,
        value: p.carbohydrates.value + c.carbohydrates.value,
        unit: c.carbohydrates.unit,
      },
      fats: {
        percentage: p.fats.percentage + c.fats.percentage,
        value: p.fats.value + c.fats.value,
        unit: c.fats.unit,
      },
    };
  });

  return tempData;
};

const getAverageGoals = (data) => {
  if (!data || data.length === 0) {
    return {
      calories: {
        percentage: 0,
        value: 0,
        unit: "kcal",
      },
      protein: {
        percentage: 0,
        value: 0,
        unit: "g",
      },
      carbohydrates: {
        percentage: 0,
        value: 0,
        unit: "g",
      },
      fats: {
        percentage: 0,
        value: 0,
        unit: "g",
      },
    };
  }

  const tempData = data.reduce((p, c) => {
    return {
      calories: {
        percentage: p.calories.percentage + c.calories.percentage,
        value: p.calories.value + c.calories.value,
        unit: c.calories.unit,
        kcal: p.calories.kcal + c.calories.kcal,
      },
      protein: {
        percentage: p.protein.percentage + c.protein.percentage,
        value: p.protein.value + c.protein.value,
        unit: c.protein.unit,
        kcal: p.protein.kcal + c.protein.kcal,
      },
      carbohydrates: {
        percentage: p.carbohydrates.percentage + c.carbohydrates.percentage,
        value: p.carbohydrates.value + c.carbohydrates.value,
        unit: c.carbohydrates.unit,
        kcal: p.carbohydrates.kcal + c.carbohydrates.kcal,
      },
      fats: {
        percentage: p.fats.percentage + c.fats.percentage,
        value: p.fats.value + c.fats.value,
        unit: c.fats.unit,
        kcal: p.fats.kcal + c.fats.kcal,
      },
    };
  });

  tempData.calories.percentage = tempData.calories.percentage / data.length;
  tempData.protein.percentage = tempData.protein.percentage / data.length;
  tempData.carbohydrates.percentage =
    tempData.carbohydrates.percentage / data.length;
  tempData.fats.percentage = tempData.fats.percentage / data.length;

  tempData.calories.value = tempData.calories.value / data.length;
  tempData.protein.value = tempData.protein.value / data.length;
  tempData.carbohydrates.value = tempData.carbohydrates.value / data.length;
  tempData.fats.value = tempData.fats.value / data.length;

  return tempData;
};

export const mapStateToProps = (state) => {
  return {
    goalsList: getGoalsList(state),
    status: getGoalsStatus(state),
    currentDailyTargets: getCurrentDailyTargets(state),
    canViewTargetsHistory: canViewTargetsHistory(state),
    canCreateNewTarget: canCreateNewTarget(state),
  };
};

export const mapDispatchToProps = {
  fetchGoals,
  resetState,
  updateDailyTargets,
};
